import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import TransitionPortal from '@/components/Common/TransitionPortal';
import List from '@/components/List/List';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useFilterItemsStore from '@/stores/FilterItemsStore';
import useToastStore from '@/stores/pure/ToastStore';
import { TemplateDetectionStatus } from '@/types/filterItems';

import FilterItemText from '../FilterItemText';

type FilterItemsTemplateDetectionModalProps = {
  isOpen: boolean;
  onConfirm: (addedTexts: string[]) => void;
  closeModal: () => void;
  existingTexts: string[];
};

const MODAL_MARGIN = 32;

const FilterItemsTemplateDetectionModal = (
  props: FilterItemsTemplateDetectionModalProps,
) => {
  const color = useColor();
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));
  const {
    templateDetectionStatus,
    detectedTemplates,
    deleteDetectedTemplate,
    startTemplateDetection,
    setTemplateDetection,
  } = useFilterItemsStore();

  const { isLoading: isLoadingTemplateDetectionStatus } = useQuery({
    queryKey: queryKeys.filterItems.FILTER_ITEMS_TEMPLATE_DETECTION,
    queryFn: async () => {
      const data =
        await service.filterItems.fetchAnalysisTextsTemplateDetection();

      if (data) {
        setTemplateDetection(
          data.template_detection_status,
          data.templates || [],
        );
      }

      return data;
    },
  });

  const handleCloseModal = () => {
    props.closeModal();
  };

  const handelClickConfirm = async () => {
    const filteredTemplates = detectedTemplates.filter(
      (template) => !props.existingTexts.includes(template.text),
    );

    await resetResults();

    props.onConfirm(filteredTemplates.map((template) => template.text));
    handleCloseModal();
  };

  const handleClickResetResults = async () => {
    await resetResults();
  };

  const resetResults = async () => {
    try {
      await service.filterItems.resetTemplateDetection();
      setTemplateDetection(
        TemplateDetectionStatus.TEMPLATE_DETECTION_STATUS_INITIAL,
        [],
      );
    } catch (e) {
      addDefaultToast(
        'Failed to reset AI detection. Please try again later.',
        'fail',
      );
    }
  };

  const handleClickStartTemplateDetection = async () => {
    try {
      await service.filterItems.triggerAnalysisFilterTextsTemplateDetection();
      startTemplateDetection();
    } catch (e) {
      addDefaultToast(
        'Failed to start AI detection. Please try again later.',
        'fail',
      );
    }
  };

  const handleDeleteDetectedTemplate = (index: number) => {
    deleteDetectedTemplate(index);
  };

  return (
    <TransitionPortal
      layerId="modal-layer"
      status={props.isOpen}
      close={props.closeModal}
      duration={300}
      style={{
        top: MODAL_MARGIN,
        left: MODAL_MARGIN,
        minWidth: '1200px',
        width: `calc(100vw - ${MODAL_MARGIN * 2}px)`,
        height: `calc(100vh - ${MODAL_MARGIN * 2}px)`,
        borderRadius: 20,
        boxShadow: 'none',
        padding: 40,
      }}
      background={{
        enable: true,
        style: {
          backdropFilter: 'blur(14px)',
          backgroundColor: color.FD40,
        },
      }}
    >
      <VFlex height="100%">
        <Flex j="space-between">
          <div>
            <Heading type="h2" className="text-gray-900">
              Use AI to suggest texts for the exclusion list
            </Heading>
            <Space h={8} />
            <Typo type="label3" className="text-gray-800">
              Let Syncly AI automatically exclude irrelevant texts, maintain
              your data hygiene for optimized analysis.
            </Typo>
          </div>

          <IconButton icon={<CloseOutlined />} onClick={props.closeModal} />
        </Flex>
        <Space h={32} />

        <VFlex
          padding="10px 16px"
          style={{
            backgroundColor: color.green(0.05),
            borderRadius: 8,
            border: `1px solid ${color.gray300}`,
          }}
        >
          {templateDetectionStatus ===
          TemplateDetectionStatus.TEMPLATE_DETECTION_STATUS_INITIAL ? (
            <Flex a="center" j="space-between">
              <Flex a="center">
                <AIIcon className="text-base" />
                <Space w={4} />
                <Typo
                  type="label3"
                  weight="b"
                  ellipsis
                  className="text-gray-700"
                >
                  Detect noise by Syncly AI
                </Typo>
              </Flex>

              <Button
                variant="primary"
                sizeType="s"
                onClick={handleClickStartTemplateDetection}
              >
                Start
              </Button>
            </Flex>
          ) : templateDetectionStatus ===
            TemplateDetectionStatus.TEMPLATE_DETECTION_STATUS_RUNNING ? (
            <>
              <Flex a="center" j="space-between">
                <Flex a="center">
                  <Spinner size={12} />
                  <Space w={4} />
                  <Typo
                    type="label3"
                    weight="b"
                    ellipsis
                    className="text-gray-700"
                  >
                    AI is scanning your entire data to detect noise...
                  </Typo>
                </Flex>
              </Flex>
              <Space h={16} />
              <Typo type="label3" weight="r" className="text-gray-700">
                AI will focus on finding texts and phrases that are being
                repeated without contributing to the context of feedback.
              </Typo>
            </>
          ) : templateDetectionStatus ===
            TemplateDetectionStatus.TEMPLATE_DETECTION_STATUS_COMPLETE ? (
            <>
              <Flex a="center" j="space-between">
                <Flex a="center">
                  <AIIcon className="text-base" />
                  <Space w={4} />
                  <Typo
                    type="label3"
                    weight="b"
                    ellipsis
                    className="text-gray-700"
                  >
                    Check the suggested words and phrases
                  </Typo>
                </Flex>
              </Flex>
              <Space h={16} />
              <Typo type="label3" weight="r" className="text-gray-700">
                Review and delete only the texts you'd like to analyze. Press
                confirm after review.
              </Typo>
            </>
          ) : (
            <Flex a="center" j="space-between">
              <Flex a="center">
                <AIIcon className="text-base" />
                <Space w={4} />
                <Typo
                  type="label3"
                  weight="b"
                  ellipsis
                  className="text-gray-700"
                >
                  AI detection failed
                </Typo>
              </Flex>
              <Button
                variant="primary"
                sizeType="s"
                onClick={handleClickStartTemplateDetection}
              >
                Retry
              </Button>
            </Flex>
          )}
        </VFlex>

        <Space h={32} />

        <Transition
          style={{
            flex: 1,
            height: '100%',
            overflow: 'auto',
          }}
          status={!isLoadingTemplateDetectionStatus}
          fallback={
            <CenterFlex style={{ height: '100%' }}>
              <Spinner />
            </CenterFlex>
          }
        >
          <>
            {templateDetectionStatus ===
              TemplateDetectionStatus.TEMPLATE_DETECTION_STATUS_COMPLETE && (
              <List
                data={detectedTemplates}
                className="gap-4 overflow-unset"
                render={(template, index) => (
                  <VFlex g={6}>
                    <Typo type="label3" className="text-gray-700">
                      From &nbsp;
                      <Typo type="label3" weight="b" className="text-gray-700">
                        {template.feedback_count}
                      </Typo>
                      &nbsp; Feedback
                    </Typo>

                    <FilterItemText
                      readOnly
                      key={index}
                      text={template.text}
                      actionButton={
                        <IconButton
                          className="delete-button"
                          icon={<DeleteOutlined />}
                          outlined={false}
                          onClick={() => {
                            handleDeleteDetectedTemplate(index);
                          }}
                          sizeType="l"
                          style={{
                            flexShrink: 0,
                          }}
                        />
                      }
                    />
                  </VFlex>
                )}
              />
            )}
          </>
        </Transition>

        <Space h={40} />

        {templateDetectionStatus ==
          TemplateDetectionStatus.TEMPLATE_DETECTION_STATUS_COMPLETE && (
          <Flex g={12} j="flex-end">
            <Button
              sizeType="m"
              onClick={handleClickResetResults}
              variant="error_outlined"
            >
              Reset results
            </Button>
            <Button sizeType="m" onClick={handelClickConfirm}>
              Confirm
            </Button>
          </Flex>
        )}
      </VFlex>
    </TransitionPortal>
  );
};

export default FilterItemsTemplateDetectionModal;
